/**
 * venueCoverage — declarative mapping from a safe-venue category to the
 * compliance prompts the venue inherently helps satisfy.
 *
 * The pre-meet compliance step asks the member to acknowledge four
 * items: 18+, public place / public time, exit plan, and knowing
 * how to reach safety resources. Different venue categories provide
 * different baseline coverage:
 *
 *   - cafe / bar_no_alcohol  → public, exit plan (open layout, staff)
 *   - library / community_center → public, exit plan, support resources
 *   - park                   → public only (no built-in support)
 *   - hotel_lobby            → public, exit plan, support resources (concierge)
 *
 * This is presentation-only — actual compliance still requires the
 * member to acknowledge each prompt in StepCompliance. The coverage
 * surface just communicates "this venue helps you here, you still need
 * to handle that".
 */

export type CompliancePromptKey =
  | "ackEighteen"
  | "ackPublic"
  | "ackEscape"
  | "ackSafetyResources";

export interface CoverageItem {
  key: CompliancePromptKey;
  /** 'covered' = venue inherently satisfies; 'partial' = helps but not full;
   *  'self'   = venue can't satisfy, member must own it. */
  level: "covered" | "partial" | "self";
  /** One-line explainer rendered in the venue card. */
  note: string;
}

export const PROMPT_LABELS: Record<CompliancePromptKey, string> = {
  ackEighteen: "18+ attendees",
  ackPublic: "Public place",
  ackEscape: "Exit plan",
  ackSafetyResources: "Support resources",
};

const SELF_18 = (): CoverageItem => ({
  key: "ackEighteen",
  level: "self",
  note: "You confirm both attendees are 18+.",
});

export const VENUE_COVERAGE: Record<string, CoverageItem[]> = {
  cafe: [
    SELF_18(),
    {
      key: "ackPublic",
      level: "covered",
      note: "Open café floor with staff present during posted hours.",
    },
    {
      key: "ackEscape",
      level: "covered",
      note: "Multiple exits, walkable street, easy to step outside.",
    },
    {
      key: "ackSafetyResources",
      level: "partial",
      note: "Staff can call emergency services if asked.",
    },
  ],
  bar_no_alcohol: [
    SELF_18(),
    {
      key: "ackPublic",
      level: "covered",
      note: "Public venue, alcohol-free environment reduces risk.",
    },
    {
      key: "ackEscape",
      level: "covered",
      note: "Bar staff and bouncers visible; doors stay accessible.",
    },
    {
      key: "ackSafetyResources",
      level: "partial",
      note: "Staff trained to respond to incidents.",
    },
  ],
  library: [
    SELF_18(),
    {
      key: "ackPublic",
      level: "covered",
      note: "Civic public space with staff and security.",
    },
    {
      key: "ackEscape",
      level: "covered",
      note: "Multiple exits and quiet rooms; you can leave without notice.",
    },
    {
      key: "ackSafetyResources",
      level: "covered",
      note: "Many libraries host social-services info and Wi-Fi for help-line access.",
    },
  ],
  park: [
    SELF_18(),
    {
      key: "ackPublic",
      level: "partial",
      note: "Public, but coverage varies by daylight and crowd.",
    },
    {
      key: "ackEscape",
      level: "partial",
      note: "Open paths help, but transport home is on you.",
    },
    {
      key: "ackSafetyResources",
      level: "self",
      note: "No on-site staff — keep crisis lines saved on your phone.",
    },
  ],
  hotel_lobby: [
    SELF_18(),
    {
      key: "ackPublic",
      level: "covered",
      note: "Lobby is public 24/7 with concierge and security cameras.",
    },
    {
      key: "ackEscape",
      level: "covered",
      note: "Concierge can call a cab; lobby exits to a busy street.",
    },
    {
      key: "ackSafetyResources",
      level: "covered",
      note: "Front desk can contact emergency services on your behalf.",
    },
  ],
  community_center: [
    SELF_18(),
    {
      key: "ackPublic",
      level: "covered",
      note: "Staffed civic space during posted hours.",
    },
    {
      key: "ackEscape",
      level: "covered",
      note: "Open layout, staff on duty, easy to exit.",
    },
    {
      key: "ackSafetyResources",
      level: "covered",
      note: "Often co-located with social services and crisis referrals.",
    },
  ],
};

/** Fallback for unknown categories — assume nothing. */
const UNKNOWN_COVERAGE: CoverageItem[] = [
  SELF_18(),
  {
    key: "ackPublic",
    level: "self",
    note: "Confirm this venue is public during your meet time.",
  },
  {
    key: "ackEscape",
    level: "self",
    note: "Plan your own transport and exit route.",
  },
  {
    key: "ackSafetyResources",
    level: "self",
    note: "Save crisis lines from /safety before you go.",
  },
];

export function getVenueCoverage(category: string | null | undefined): CoverageItem[] {
  if (!category) return UNKNOWN_COVERAGE;
  return VENUE_COVERAGE[category] ?? UNKNOWN_COVERAGE;
}

/** Quick aggregate: how many of the 4 prompts the venue actively covers. */
export function coverageScore(category: string | null | undefined): {
  covered: number;
  partial: number;
  self: number;
  total: number;
} {
  const items = getVenueCoverage(category);
  return {
    covered: items.filter((i) => i.level === "covered").length,
    partial: items.filter((i) => i.level === "partial").length,
    self: items.filter((i) => i.level === "self").length,
    total: items.length,
  };
}
